// sherlock-subscription.js
// NZSC-3047/3050 자산원클릭(셜록) 구독형 가격제 — 조회 + 액션 Vuex 모듈.
// FE-1: 조회 API (GET subscription / plans)
// FE-2: 액션 API (subscribe / upgrade / downgrade / cancel / charge-extra / convert-from-points)
// 모든 액션 성공 후 호출자(컴포넌트)는 acGetSherlockSubscription() 으로 구독 정보 재조회 책임.
import SherlockConstant, {
  initialSubscriptionInfoState,
} from "@/mixins/sherlock/const/sherlockConstant.js";
import SherlockAcConstant from "@/mixins/sherlock/const/sherlockAcConstant.js";
import { callGetApi, callPostApi } from "@/utils/apiWrapper";

export const state = {
  _subscription: {
    // GET /sherlock/subscription 응답
    info: { ...initialSubscriptionInfoState },
    // GET /sherlock/subscription/plans 응답 (베이직 → 프리미엄 → 엔터프라이즈 순)
    plans: [],
  },
};

export const mutations = {
  [SherlockConstant.SET_SHERLOCK_SUBSCRIPTION_INFO](state, info) {
    state._subscription.info = { ...initialSubscriptionInfoState };
    if (info) {
      state._subscription.info = { ...info };
    }
  },
  [SherlockConstant.SET_SHERLOCK_SUBSCRIPTION_PLANS](state, plans) {
    state._subscription.plans = [];
    if (plans?.length) {
      state._subscription.plans = plans;
    }
  },
};

export const actions = {
  /**************************************************************
   * 구독 정보 조회
   * - hasSubscription=false → 미결제(가입 전) 화면 분기
   * - status='구독중'/'해지예약' + plan != null → 구독중 화면 분기
   * - 별도 "포인트 소진 중" 세그먼트는 BE 응답으로 직접 판단 어려움 →
   *   상위 view 에서 hasSubscription=false && 보유 포인트 > 0 으로 분기.
   **************************************************************/
  async acGetSherlockSubscription({ commit }) {
    const res = await callGetApi({
      url: `${SherlockAcConstant.GET_SHERLOCK_SUBSCRIPTION}`,
      params: {},
      commit,
    });
    commit(SherlockConstant.SET_SHERLOCK_SUBSCRIPTION_INFO, res);
    return res;
  },

  /**************************************************************
   * 구독 사용내역 조회 — 구독 사용 + 추가결제 통합 목록 (createDate desc, 최대 100건).
   * 응답: [{ action, detail, createDate }].
   **************************************************************/
  async acGetSherlockSubscriptionUsageHistory({ commit }) {
    const res = await callGetApi({
      url: `${SherlockAcConstant.GET_SHERLOCK_SUBSCRIPTION_USAGE_HISTORY}`,
      params: {},
      commit,
    });
    return Array.isArray(res) ? res : [];
  },

  /**************************************************************
   * 구독 플랜 목록 조회 (베이직/프리미엄/엔터프라이즈)
   **************************************************************/
  async acGetSherlockSubscriptionPlans({ commit }) {
    const res = await callGetApi({
      url: `${SherlockAcConstant.GET_SHERLOCK_SUBSCRIPTION_PLANS}`,
      params: {},
      commit,
    });
    // BE 응답: { plans: [...] } 또는 [...] 형태 모두 대응
    const plans = Array.isArray(res) ? res : res?.plans || [];
    commit(SherlockConstant.SET_SHERLOCK_SUBSCRIPTION_PLANS, plans);
    return plans;
  },

  /**************************************************************
   * [FE-2] 신규 구독 시작 — 빌링키 + 첫 결제.
   * billingKey 미전달 시 기본카드 fallback (BE 정책). 응답: SubscribeResponseDto.
   **************************************************************/
  async acSubscribeSubscription({ commit }, { productCode, pgName, billingKey } = {}) {
    return callPostApi({
      url: `${SherlockAcConstant.POST_SHERLOCK_SUBSCRIPTION_SUBSCRIBE}`,
      data: { productCode, pgName, billingKey },
      commit,
    });
  },

  /**************************************************************
   * [FE-2] 베이직 → 프리미엄 즉시 업그레이드 (일할 차액 결제).
   * 성공 시 즉시 플랜 교체 + 새 한도 적용. 응답: UpgradeResponseDto.
   **************************************************************/
  async acUpgradeSubscription({ commit }, { targetProductCode } = {}) {
    return callPostApi({
      url: `${SherlockAcConstant.POST_SHERLOCK_SUBSCRIPTION_UPGRADE}`,
      data: { targetProductCode },
      commit,
    });
  },

  /**************************************************************
   * [FE-2] 프리미엄 → 베이직 다운그레이드 예약 (다음 결제일 적용).
   * 즉시 결제·플랜 교체 없음. 응답: DowngradeResponseDto.
   **************************************************************/
  async acDowngradeSubscription({ commit }, { targetProductCode } = {}) {
    return callPostApi({
      url: `${SherlockAcConstant.POST_SHERLOCK_SUBSCRIPTION_DOWNGRADE}`,
      data: { targetProductCode },
      commit,
    });
  },

  /**************************************************************
   * [FE-2] 다운그레이드 예약 취소.
   * 결제일 도래 전까지 호출 가능. 응답: 200 (body 없음).
   **************************************************************/
  async acCancelDowngradeSubscription({ commit }) {
    return callPostApi({
      url: `${SherlockAcConstant.POST_SHERLOCK_SUBSCRIPTION_DOWNGRADE_CANCEL}`,
      data: {},
      commit,
    });
  },

  /**************************************************************
   * [FE-2] 구독 해지 예약 (다음 결제일부터 자동갱신 중단).
   * reasons: CancelReason[] (1+ 필수, 유니크). feedback: 4택 사유 선택 시 필수.
   * 응답: CancelSubscriptionResponseDto.
   **************************************************************/
  async acCancelSubscription({ commit }, { reasons, feedback } = {}) {
    return callPostApi({
      url: `${SherlockAcConstant.POST_SHERLOCK_SUBSCRIPTION_CANCEL}`,
      data: { reasons, feedback },
      commit,
    });
  },

  /**************************************************************
   * [FE-2] 해지 예약 철회 (결제일 도래 전까지 호출 가능).
   * 응답: 200 (body 없음).
   **************************************************************/
  async acRevokeCancelSubscription({ commit }) {
    return callPostApi({
      url: `${SherlockAcConstant.POST_SHERLOCK_SUBSCRIPTION_REVOKE_CANCEL}`,
      data: {},
      commit,
    });
  },

  /**************************************************************
   * [FE-2] 초과 1건 자동결제.
   * category: 'multi_dwelling' | 'register' | 'delinquency'.
   * 응답: ChargeExtraResponseDto (paid=true 만 200, 실패는 4xx).
   **************************************************************/
  // [NZSC-3047] 단건 초과 결제(count=1, default).
  // [NZSC-3103] count: 다건 한도 초과 시 부족 건수(N) 추가 결제. 단건은 default 1.
  async acChargeExtra({ commit }, { category, count = 1 } = {}) {
    return callPostApi({
      url: `${SherlockAcConstant.POST_SHERLOCK_SUBSCRIPTION_CHARGE_EXTRA}`,
      data: { category, count },
      commit,
    });
  },

  /**************************************************************
   * [FE-2] 포인트 → 이용권 치환 + 구독 시작.
   * 응답: ConvertFromPointsResponseDto (pointConversion.discardedPoint 안내 표시).
   **************************************************************/
  async acConvertFromPoints({ commit }, { productCode, pgName, billingKey } = {}) {
    return callPostApi({
      url: `${SherlockAcConstant.POST_SHERLOCK_SUBSCRIPTION_CONVERT_FROM_POINTS}`,
      data: { productCode, pgName, billingKey },
      commit,
    });
  },
};

export const getters = {
  /** 베이직 플랜 (productCode 기준 — 첫 번째 self_employed 도 fallback) */
  getBasicPlan: state =>
    state._subscription.plans.find(p => p.recommendation === "self_employed") ||
    state._subscription.plans.find(p => p.productCode?.includes("basic")) ||
    null,

  /** 프리미엄 플랜 (most_popular) */
  getPremiumPlan: state =>
    state._subscription.plans.find(p => p.recommendation === "most_popular") ||
    state._subscription.plans.find(p => p.productCode?.includes("premium")) ||
    null,

  /** 엔터프라이즈 플랜 (sales contactType) */
  getEnterprisePlan: state =>
    state._subscription.plans.find(p => p.contactType === "sales") ||
    state._subscription.plans.find(p => p.recommendation === "enterprise") ||
    null,
};

export default {
  namespaced: true,
  state,
  mutations,
  actions,
  getters,
};
